const SECTION_KEYS = {
  Overview: ['legal_name', 'jurisdiction'],
  Registration: ['registered_agent', 'address'],
  Address: ['registered_office']
}

const _section = (label) => ({
  attr: { 'data-content-section': label },
  flow: 'y',
  gap: 'A',
  padding: 'D',
  round: 'C',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',

  CiTitle: {
    tag: 'h2',
    margin: '0 0 B 0',
    fontSize: 'C',
    fontWeight: '800',
    color: 'title',
    letterSpacing: '-0.01em',
    text: label
  },

  Rows: {
    flow: 'y',
    childExtends: 'CompanyInfoRow',
    childrenAs: 'state',
    children: (el, s) => {
      const wanted = SECTION_KEYS[label] || []
      return (s.root.companyInfo || []).filter((r) => wanted.includes(r.key))
    }
  }
})

const RESOURCES = [
  { label: 'Pitch Deck', url: 'https://docs.google.com/presentation/d/1oarPeQXg-s1uxXYLBQ8pdtq0V8Y-PXlXjYUwB_n5SZY/edit', icon: 'document' },
  { label: 'Landing Page', url: 'https://dev.symbols.app', icon: 'globe' },
  { label: 'Platform Docs', url: 'https://dev.symbols.app/docs/intro', icon: 'book' },
  { label: 'SDK Docs', url: 'https://docs.google.com/document/d/13IAfzS9eUjapnr5PVK6AVZPkRtjTYG9An7P9GMgRrM8/edit', icon: 'code' },
  { label: 'Figma UI Library', url: 'https://www.figma.com/file/LDFnKQSxUxZeeLHt2OT67r?node-id=0:1&locale=en&type=design', icon: 'eye' },
  { label: 'GitHub Organization', url: 'https://github.com/symbo-ls', icon: 'github' },
  { label: 'npm Packages', url: 'https://www.npmjs.com/org/symbo.ls', icon: 'code' }
]

export const company = {
  extends: 'ContentPage',
  metadata: { title: 'Company / Symbols Workspace' },
  eyebrow: 'About',
  title: 'Company',
  sections: [
    { label: 'Overview' },
    { label: 'Valuation' },
    { label: 'Registration' },
    { label: 'Address' },
    { label: 'Resources' }
  ],

  PageBody: {
    flow: 'y',
    gap: 'C',
    flex: '1',
    minWidth: '0',
    maxWidth: 'I',

    OverviewCard: _section('Overview'),

    ValuationCard: {
      attr: { 'data-content-section': 'Valuation' },
      flow: 'y',
      gap: 'A',
      padding: 'D',
      round: 'C',
      background: 'overlay',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',

      CiTitle: {
        tag: 'h2',
        margin: '0 0 B 0',
        fontSize: 'C',
        fontWeight: '800',
        color: 'title',
        letterSpacing: '-0.01em',
        text: 'Valuation'
      },

      CurrentVal: {
        flow: 'x',
        align: 'baseline',
        gap: 'A',
        Amount: {
          tag: 'span',
          fontSize: 'E',
          fontWeight: '800',
          color: 'title',
          letterSpacing: '-0.04em',
          lineHeight: '1',
          text: (el, s) => {
            const v = s.root?.companySettings?.current_valuation
            return v ? `$${Number(v).toLocaleString()}` : '—'
          }
        },
        Label: {
          tag: 'span',
          fontSize: 'A',
          color: 'caption',
          fontWeight: '600',
          text: 'current valuation'
        }
      },

      Description: {
        tag: 'p',
        fontSize: 'Z2',
        color: 'caption',
        lineHeight: '1.5em',
        padding: '0 0 B 0',
        margin: '0',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'line',
        text: 'Current valuation is determined by the resources and time invested to the building the product, campaigns and brand.'
      },

      RoundsTitle: {
        tag: 'h3',
        fontSize: 'A',
        fontWeight: '700',
        color: 'subtitle',
        margin: 'A 0 0 0',
        text: 'Valuation rounds'
      },

      Rounds: {
        flow: 'y',
        childrenAs: 'state',
        children: (el, s) => {
          const v = s.root?.valuations
          return v ? v.slice().sort((a, b) => b.amount - a.amount) : []
        },
        childExtends: {
          flow: 'x',
          align: 'center space-between',
          gap: 'B',
          padding: 'A 0',
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          borderBottomColor: 'line',

          Left: {
            flow: 'x',
            align: 'center',
            gap: 'Z',
            RoundLabel: {
              tag: 'span',
              fontSize: 'A',
              fontWeight: '700',
              color: 'title',
              text: (el, s) => s.label || '—'
            },
            RoundType: {
              tag: 'span',
              fontSize: 'Y2',
              padding: 'V Z',
              round: 'V',
              fontWeight: '700',
              color: 'caption',
              background: 'overlayStrong',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'line',
              text: (el, s) => s.round_type || ''
            },
            CurrentTag: {
              hide: (el, s) => s.is_current !== true,
              tag: 'span',
              fontSize: 'Y2',
              padding: 'V Z',
              round: 'V',
              fontWeight: '700',
              color: 'green',
              background: 'green.12',
              text: 'CURRENT'
            }
          },

          Right: {
            flow: 'y',
            align: 'flex-end',
            gap: 'V',
            Amount: {
              tag: 'span',
              fontSize: 'A',
              fontWeight: '700',
              color: 'title',
              text: (el, s) => `$${Number(s.amount || 0).toLocaleString()}`
            },
            DateText: {
              tag: 'span',
              fontSize: 'Y2',
              color: 'caption',
              text: (el, s) => s.effective_date || ''
            }
          }
        }
      }
    },

    RegistrationCard: _section('Registration'),
    AddressCard: _section('Address'),

    ResourcesCard: {
      ..._section('Resources'),
      Rows: null,

      Links: {
        flow: 'y',
        childrenAs: 'state',
        children: () => RESOURCES,
        childExtends: {
          extends: 'Link',
          href: (el, s) => s.url,
          target: '_blank',
          textDecoration: 'none',
          flow: 'x',
          align: 'center space-between',
          gap: 'B',
          padding: 'A 0',
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          borderBottomColor: 'line',
          ':hover': { background: 'overlay' },

          Left: {
            flow: 'x',
            align: 'center left',
            gap: 'A',
            Icon: {
              icon: (el, s) => s.icon || 'link',
              boxSize: 'A1',
              color: 'blue'
            },
            Label: {
              tag: 'span',
              fontSize: 'B',
              fontWeight: '600',
              color: 'title',
              text: (el, s) => s.label
            }
          },
          Arrow: {
            tag: 'span',
            fontSize: 'Z2',
            color: 'caption',
            text: '\u2192'
          }
        }
      }
    }
  }
}
